import React, { useCallback, useRef, useState } from 'react';
import { carrierColor, rowTone } from './hsColors';
import { MetalBadge, TypeBadge } from './hsBadges';

// =============================================================================
// HealthSherpa plan list (5.18.0, 5.18.1) — formal, calm, easy to read for staff and customers at the screen.
// 5.18.1: the table uses the FULL width of the window. The Plan column takes the remaining space; every
// other data column has a default width and a drag handle on its header's right edge (the Plan handle
// moves the Plan | Metal boundary). Widths are in em, so they scale with the text-size slider (14–28px),
// and are remembered per browser (localStorage) until "Reset columns". Below the sum of the minimum widths
// the wrapper scrolls sideways instead of squashing.
// Plan names are never truncated: they wrap fully and rows grow to fit (cells vertically centered).
// Everything inside is in em (text, badges, logo tiles, checkbox, buttons, padding); no text is smaller
// than 0.85em. A clearly visible 1px divider between rows (hsColors ROW.divider) — drawn on the cells, so it
// stays visible on hovered and ticked rows. Hover / keyboard focus highlight the whole row.
// =============================================================================

export const ROW_FONT_MIN = 14;
export const ROW_FONT_MAX = 28;
export const ROW_FONT_DEFAULT = 17;
const FONT_KEY = 'insuredocs.hs.rowFont.v2';        // 5.18.1: new key, so everyone starts at the new 17px default
const WIDTHS_KEY = 'insuredocs.hs.colWidths.v1';    // { [column key]: width in em } — only resizable columns
const MAX_COL_EM = 60;

export const loadRowFont = () => {
  try {
    const v = Number(window.localStorage.getItem(FONT_KEY));
    return v >= ROW_FONT_MIN && v <= ROW_FONT_MAX ? v : ROW_FONT_DEFAULT;
  } catch (_e) {
    return ROW_FONT_DEFAULT;
  }
};
export const saveRowFont = (v) => { try { window.localStorage.setItem(FONT_KEY, String(v)); } catch (_e) { /* storage unavailable */ } };

// Columns. `em` = default width, `min` = minimum width (em). `flex` = takes the remaining space (Plan).
// `fixed` = not resizable (tick box, Present, Details). `num` = right-aligned numbers.
export const COLS = [
  { key: 'tick', fixed: 2.4 },
  { key: 'carrier', label: 'Carrier', em: 8.6, min: 6 },
  { key: 'plan', label: 'Plan', flex: true, min: 17 },
  { key: 'metal', label: 'Metal', em: 9, min: 8.8 },          // "Expanded Bronze" wraps to 2 lines; min fits "Catastrophic"
  { key: 'type', label: 'Type', em: 4.8, min: 4.7 },
  { key: 'premium', label: 'Premium', em: 7, min: 6.9, num: true },   // min fits "$10,600.00" (bold)
  { key: 'credit', label: 'Est. tax credit*', em: 7, min: 6.9, num: true },
  { key: 'net', label: 'Est. net*', em: 7, min: 6.9, num: true },
  { key: 'deductible', label: 'Deductible', em: 7, min: 6.9, num: true },
  { key: 'moop', label: 'Out-of-pocket max', em: 7, min: 6.9, num: true },
  { key: 'hsa', label: 'HSA', em: 4, min: 3.6 },
  { key: 'present', fixed: 5.4 },
  { key: 'details', fixed: 5.2 },
];
const RESIZABLE = COLS.filter((c) => c.em !== undefined);
const colByKey = (k) => COLS.find((c) => c.key === k);
const clampWidth = (key, em) => {
  const c = colByKey(key);
  return Math.round(Math.min(MAX_COL_EM, Math.max(c.min, Number(em))) * 100) / 100;
};
export const defaultWidths = () => Object.fromEntries(RESIZABLE.map((c) => [c.key, c.em]));

export const loadColWidths = () => {
  const w = defaultWidths();
  try {
    const stored = JSON.parse(window.localStorage.getItem(WIDTHS_KEY) || '{}');
    for (const c of RESIZABLE) {
      const v = Number(stored && stored[c.key]);
      if (Number.isFinite(v) && v > 0) w[c.key] = clampWidth(c.key, v);
    }
  } catch (_e) { /* storage unavailable or unreadable: defaults */ }
  return w;
};
const saveColWidths = (w) => { try { window.localStorage.setItem(WIDTHS_KEY, JSON.stringify(w)); } catch (_e) { /* storage unavailable */ } };
const clearColWidths = () => { try { window.localStorage.removeItem(WIDTHS_KEY); } catch (_e) { /* storage unavailable */ } };

// Column widths state for the plan list (Results owns it, so the toolbar can show "Reset columns").
export function useColumnWidths() {
  const [widths, setWidths] = useState(loadColWidths);
  const latest = useRef(widths);
  latest.current = widths;
  const setWidth = useCallback((key, em, persist) => {
    const next = { ...latest.current, [key]: clampWidth(key, em) };
    latest.current = next;
    setWidths(next);
    if (persist) saveColWidths(next);
  }, []);
  const persist = useCallback(() => saveColWidths(latest.current), []);
  const reset = useCallback(() => { clearColWidths(); latest.current = defaultWidths(); setWidths(latest.current); }, []);
  const isDefault = RESIZABLE.every((c) => widths[c.key] === c.em);
  return { widths, setWidth, persist, reset, isDefault };
}

const usd = (v) => (v === null || v === undefined || Number.isNaN(Number(v)))
  ? '—'
  : `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const netCell = (v) => (v !== null && v !== undefined && Math.abs(Number(v)) < 0.005) ? `${usd(v)}*` : usd(v);
const amount = (v, text) => (v !== null && v !== undefined) ? usd(v) : (text || '—');

const css = `
.hs-plans { border-collapse: collapse; table-layout: fixed; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif; }
.hs-plans th { position: relative; font-weight: 600; font-size: 0.92em; text-align: left; padding: 0.5em 0.598em; vertical-align: bottom; line-height: 1.3; }
.hs-plans th.num { text-align: right; }
.hs-plans td { padding: 0.5em 0.55em; vertical-align: middle; border-bottom: 1px solid var(--hs-divider); }
.hs-plans td.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.hs-plans td.hs-act { padding-left: 0.2em; padding-right: 0.2em; text-align: center; }
.hs-plans tr.hs-row { background: var(--hs-panel); cursor: pointer; }
.hs-plans tr.hs-row:hover, .hs-plans tr.hs-row:focus-within, .hs-plans tr.hs-row:focus-visible { background: var(--hs-hover); }
.hs-plans tr.hs-row.hs-ticked { background: var(--hs-tick); }
.hs-plans tr.hs-row.hs-ticked:hover, .hs-plans tr.hs-row.hs-ticked:focus-within, .hs-plans tr.hs-row.hs-ticked:focus-visible { background: var(--hs-tick-hover); }
.hs-plans tr.hs-row:focus-visible { outline: 2px solid var(--hs-focus); outline-offset: -2px; }
.hs-plans .hs-btn { -webkit-appearance: none; appearance: none; font: inherit; font-size: 0.9em; padding: 0.28em 0.65em; border-radius: 0.3em; cursor: pointer;
  background: transparent; color: var(--hs-text); border: 1px solid var(--hs-divider); white-space: nowrap; }
.hs-plans .hs-btn:hover { background: var(--hs-tick); }
.hs-plans .hs-name { display: block; font-weight: 600; line-height: 1.35; overflow-wrap: break-word; }
.hs-plans .hs-carrier-name { display: block; font-size: 0.88em; line-height: 1.3; color: var(--hs-subtext); overflow-wrap: break-word; }
.hs-plans .hs-grip { position: absolute; top: 0; bottom: 0; right: -0.4em; width: 0.8em; cursor: col-resize; z-index: 2; touch-action: none; outline: none; }
.hs-plans .hs-grip::after { content: ''; position: absolute; top: 25%; bottom: 25%; left: calc(50% - 1px); width: 2px; border-radius: 1px; background: var(--hs-divider); }
.hs-plans .hs-grip:hover::after, .hs-plans .hs-grip:focus-visible::after, .hs-plans .hs-grip.hs-dragging::after { top: 0; bottom: 0; background: var(--hs-focus); }
`;

// The carrier logo, from the full-size stored image (up to 640×160) shown about 2.2em tall — never a
// scaled-up thumbnail. It shrinks with a narrow Carrier column. Dark mode: a small white tile (never recolored).
function CarrierLogo({ url, name, darkMode }) {
  return (
    <span style={{ display: 'inline-flex', maxWidth: '100%', boxSizing: 'border-box', lineHeight: 0,
      ...(darkMode ? { background: '#fff', borderRadius: '0.3em', padding: '0.18em 0.35em' } : {}) }}>
      <img src={url} alt={name || ''} decoding="async"
        style={{ display: 'block', height: '2.2em', width: 'auto', maxWidth: '100%', objectFit: 'contain', objectPosition: 'left center' }} />
    </span>
  );
}

export default function PlanTable({ plans, selected, onToggle, onPresent, onDetails, darkMode, fontPx, widths, setWidth, persistWidths }) {
  const tone = (k) => rowTone(k, darkMode);
  const [dragging, setDragging] = useState(null);
  const vars = {
    '--hs-panel': tone('panel'), '--hs-hover': tone('hover'), '--hs-tick': tone('tick'), '--hs-tick-hover': tone('tickHover'),
    '--hs-divider': tone('divider'), '--hs-text': tone('text'), '--hs-subtext': tone('subtext'), '--hs-focus': darkMode ? '#7d9cc0' : '#4a6489',
  };
  const widthOf = (c) => (c.fixed !== undefined ? c.fixed : c.flex ? null : widths[c.key]);
  const minTableEm = COLS.reduce((a, c) => a + (c.flex ? c.min : widthOf(c)), 0);

  // The Plan handle moves the Plan | Metal boundary (Plan is the flexible column): it resizes Metal inversely.
  const targetOf = (key) => (key === 'plan' ? { key: 'metal', sign: -1 } : { key, sign: 1 });
  const startDrag = (key, e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    e.stopPropagation();
    const { key: tk, sign } = targetOf(key);
    const startX = e.clientX, startW = widths[tk], el = e.currentTarget;
    try { el.setPointerCapture(e.pointerId); } catch (_e) { /* old browser */ }
    setDragging(key);
    const move = (ev) => setWidth(tk, startW + (sign * (ev.clientX - startX)) / fontPx);
    const up = () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerup', up);
      el.removeEventListener('pointercancel', up);
      setDragging(null);
      persistWidths();
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
  };
  const keyResize = (key, e) => {
    const step = e.key === 'ArrowRight' ? 0.5 : e.key === 'ArrowLeft' ? -0.5 : 0;
    if (!step) return;
    e.preventDefault();
    const { key: tk, sign } = targetOf(key);
    setWidth(tk, widths[tk] + sign * step, true);
  };
  const grip = (c) => (
    <span className={`hs-grip${dragging === c.key ? ' hs-dragging' : ''}`} role="separator" aria-orientation="vertical" tabIndex={0}
      aria-label={`Resize the ${c.label} column (drag, or left/right arrow keys)`} title="Drag to resize this column"
      onPointerDown={(e) => startDrag(c.key, e)} onKeyDown={(e) => keyResize(c.key, e)} onClick={(e) => e.stopPropagation()} />
  );

  return (
    <div style={{ overflowX: 'auto' }}>
      <style>{css}</style>
      <table className="hs-plans" data-testid="hs-plan-table"
        style={{ ...vars, fontSize: `calc(${fontPx}px * var(--hs-scale, 1))`, width: '100%', minWidth: `${minTableEm}em`, color: tone('text'), userSelect: dragging ? 'none' : undefined }}>
        <colgroup>{COLS.map((c) => <col key={c.key} data-col={c.key} style={widthOf(c) === null ? undefined : { width: `${widthOf(c)}em` }} />)}</colgroup>
        <thead>
          <tr style={{ color: tone('subtext'), borderBottom: `2px solid ${tone('headRule')}` }}>
            {COLS.map((c) => (c.label
              ? <th key={c.key} data-col={c.key} className={c.num ? 'num' : undefined}>{c.label}{grip(c)}</th>
              : <th key={c.key} data-col={c.key} aria-label={c.key === 'tick' ? 'Ticked' : c.key === 'present' ? 'Present' : 'Details'} />))}
          </tr>
        </thead>
        <tbody>
          {plans.map((p, i) => {
            const ticked = selected.includes(p.plan_id);
            return (
              <tr key={p.plan_id} className={`hs-row${ticked ? ' hs-ticked' : ''}`} tabIndex={0}
                onClick={() => onPresent(i)}
                onKeyDown={(e) => { if (e.key === 'Enter' && e.target === e.currentTarget) { e.preventDefault(); onPresent(i); } }}
                title="Click to present this plan">
                <td onClick={(e) => e.stopPropagation()}>
                  <input type="checkbox" aria-label={`Tick ${p.name}`} checked={ticked} onChange={() => onToggle(p.plan_id)}
                    style={{ width: '1.15em', height: '1.15em', margin: 0, cursor: 'pointer', verticalAlign: 'middle' }} />
                </td>
                <td>
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '0.25em' }}>
                    {p.logo_url && <CarrierLogo url={p.logo_url} name={p.issuer_name} darkMode={darkMode} />}
                    <span className="hs-carrier-name">{p.issuer_name || '—'}</span>
                  </div>
                </td>
                <td>
                  <div style={{ border: `1px solid ${tone('nameBorder')}`, borderLeft: `0.27em solid ${carrierColor(p, darkMode)}`, borderRadius: '0.4em',
                    padding: '0.35em 0.65em' }}>
                    <span className="hs-name">{p.name}</span>
                  </div>
                </td>
                <td><MetalBadge code={p.metal_level} darkMode={darkMode} wrap /></td>
                <td><TypeBadge code={p.plan_type} darkMode={darkMode} /></td>
                <td className="num">{usd(p.gross_premium)}</td>
                <td className="num">{usd(p.subsidy_applied)}</td>
                <td className="num" style={{ fontWeight: 700 }}>{netCell(p.net_premium)}</td>
                <td className="num">{amount(p.deductible_individual, p.deductible_individual_text)}</td>
                <td className="num">{amount(p.moop_individual, p.moop_individual_text)}</td>
                <td>{p.hsa_eligible ? 'Yes' : ''}</td>
                <td className="hs-act" onClick={(e) => e.stopPropagation()}>
                  <button className="hs-btn" onClick={() => onPresent(i)}>Present</button>
                </td>
                <td className="hs-act" onClick={(e) => e.stopPropagation()}>
                  <button className="hs-btn" onClick={() => onDetails(p)}>Details</button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
